import { supabase } from './supabase';

// ─── AUTH ────────────────────────────────────────────────────────────────────

export async function signUp(email, password, username) {
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: { data: { username } }
  });
  if (error) throw error;
  if (data.user) {
    await supabase.from('profiles').upsert({ id: data.user.id, username, email });
  }
  return data;
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// ─── RESTAURANTS ─────────────────────────────────────────────────────────────

export async function fetchRestaurants() {
  const { data, error } = await supabase
    .from('restaurants')
    .select(`
      *,
      added_by_profile:profiles!restaurants_added_by_fkey (id, username),
      reviews (id, note, commentaire, created_at, user_id, profiles (username)),
      photos (url),
      user_restaurant_status (user_id, statut)
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function fetchRestaurantById(id) {
  const { data, error } = await supabase
    .from('restaurants')
    .select(`
      *,
      added_by_profile:profiles!restaurants_added_by_fkey (id, username),
      reviews (id, note, commentaire, created_at, user_id, profiles (username)),
      photos (url),
      user_restaurant_status (user_id, statut)
    `)
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

// Retourne le statut personnel d'un user sur un restaurant
// Si pas de ligne dans user_restaurant_status → "tester" par défaut
export function getStatutForUser(restaurant, userId) {
  if (!userId) return null;
  const row = restaurant.user_restaurant_status?.find(s => s.user_id === userId);
  return row?.statut ?? 'tester';
}

export async function findDuplicate(nom, adresse) {
  const nomNorm = nom.trim().toLowerCase();
  const { data } = await supabase
    .from('restaurants')
    .select('id, nom, adresse')
    .ilike('nom', `%${nomNorm}%`);

  if (!data || data.length === 0) return null;
  return data.find(r =>
    r.nom.toLowerCase().includes(nomNorm) &&
    (adresse ? r.adresse.toLowerCase().includes(adresse.split(',')[0].toLowerCase()) : true)
  ) || null;
}

export async function addRestaurant(restaurantData) {
  const { data, error } = await supabase
    .from('restaurants')
    .insert([restaurantData])
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Met à jour le statut PERSONNEL de l'utilisateur sur un restaurant
export async function setUserStatut(restaurantId, userId, statut) {
  const { error } = await supabase
    .from('user_restaurant_status')
    .upsert({ restaurant_id: restaurantId, user_id: userId, statut }, { onConflict: 'user_id,restaurant_id' });
  if (error) throw error;
}

// ─── SUPPRESSION RESTAURANT ────────────────────────────────────────────────

export async function deleteRestaurant(id) {
  // Supprime les photos du storage
  const { data: photos } = await supabase.from("photos").select("url").eq("restaurant_id", id);
  if (photos?.length) {
    const paths = photos.map(p => {
      const match = p.url.match(/restaurant-photos\/(.+)$/);
      return match ? match[1] : null;
    }).filter(Boolean);
    if (paths.length) await supabase.storage.from("restaurant-photos").remove(paths);
  }
  const { error } = await supabase.from("restaurants").delete().eq("id", id);
  if (error) throw error;
}

// ─── AVIS ────────────────────────────────────────────────────────────────────

export async function addReview(restaurantId, userId, note, commentaire) {
  const { data: existing } = await supabase
    .from('reviews')
    .select('id')
    .eq('restaurant_id', restaurantId)
    .eq('user_id', userId)
    .single();

  if (existing) {
    const { data, error } = await supabase
      .from('reviews')
      .update({ note, commentaire })
      .eq('id', existing.id)
      .select().single();
    if (error) throw error;
    await recalculerNote(restaurantId);
    return data;
  }

  const { data, error } = await supabase
    .from('reviews')
    .insert([{ restaurant_id: restaurantId, user_id: userId, note, commentaire }])
    .select().single();
  if (error) throw error;
  await recalculerNote(restaurantId);
  return data;
}

export async function deleteReview(reviewId, restaurantId) {
  const { error } = await supabase.from('reviews').delete().eq('id', reviewId);
  if (error) throw error;
  await recalculerNote(restaurantId);
}

async function recalculerNote(restaurantId) {
  const { data } = await supabase.from('reviews').select('note').eq('restaurant_id', restaurantId);
  if (!data || data.length === 0) {
    await supabase.from('restaurants').update({ note_moyenne: null, nb_avis: 0 }).eq('id', restaurantId);
    return;
  }
  const moyenne = data.reduce((s, r) => s + r.note, 0) / data.length;
  await supabase.from('restaurants')
    .update({ note_moyenne: Math.round(moyenne * 10) / 10, nb_avis: data.length })
    .eq('id', restaurantId);
}

// ─── PHOTOS ──────────────────────────────────────────────────────────────────

export async function uploadPhoto(file, restaurantId) {
  const ext = file.name.split('.').pop().toLowerCase();
  const path = `${restaurantId}/${Date.now()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from('restaurant-photos')
    .upload(path, file, { cacheControl: '3600', upsert: true, contentType: file.type });

  if (upErr) throw new Error(`Upload échoué : ${upErr.message}`);

  const { data: urlData } = supabase.storage.from('restaurant-photos').getPublicUrl(path);
  if (!urlData?.publicUrl) throw new Error("Impossible de récupérer l'URL.");

  const { error: dbErr } = await supabase.from('photos').insert([{ restaurant_id: restaurantId, url: urlData.publicUrl }]);
  if (dbErr) throw new Error(`Erreur BDD : ${dbErr.message}`);

  return urlData.publicUrl;
}

export async function fetchPhotos(restaurantId) {
  const { data, error } = await supabase.from('photos').select('*').eq('restaurant_id', restaurantId);
  if (error) throw error;
  return data;
}

// ─── GÉOCODAGE ───────────────────────────────────────────────────────────────

export async function geocodeAdresse(adresse) {
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(adresse)}&limit=1`;
    const res = await fetch(url, { headers: { 'Accept-Language': 'fr' } });
    const data = await res.json();
    if (data?.length > 0) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch {}
  return null;
}

// Autocomplétion d'adresse via Nominatim
export async function autocompleteAdresse(query) {
  if (!query || query.length < 3) return [];
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1&accept-language=fr`;
    const res = await fetch(url, { headers: { 'Accept-Language': 'fr', 'User-Agent': 'Foodies-App' } });
    const data = await res.json();
    return data.map(item => ({
      label: item.display_name,
      short: [item.address?.road, item.address?.house_number, item.address?.city || item.address?.town || item.address?.village].filter(Boolean).join(' '),
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
    }));
  } catch {
    return [];
  }
}

export async function fetchProfile(userId) {
  const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
  return data;
}
